import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FilterBarWrapper,
  FilterControlsRow,
  FilterTriggerButton,
  FilterCountBadge,
  FilterDropdown,
  FilterDropdownHeader,
  FilterDropdownTitle,
  FilterDropdownClose,
  FilterSection,
  FilterSectionLabel,
  FilterSelect,
  DateRangeRow,
  DateInput,
  DateRangeSeparator,
  FilterDropdownFooter,
  ClearFilterButton,
  ApplyFilterButton,
} from './filterBar.styles';

const TuneIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" d="M4 6h10M4 12h16M4 18h10" />
    <circle cx="17" cy="6" r="2" />
    <circle cx="10" cy="18" r="2" />
  </svg>
);

export const CloseIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
    <path strokeLinecap="round" d="M6 6l12 12M18 6 6 18" />
  </svg>
);

export const DEFAULT_FILTER_VALUES = {
  status: '',
  type: '',
  startDate: '',
  endDate: '',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'CLOSED', label: 'Closed' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'BUG', label: 'Bug' },
  { value: 'QUERY', label: 'Query' },
  { value: 'OTHER', label: 'Other' },
];
const DATE_FIELDS = ['startDate', 'endDate'];

const FilterBar = ({
  activeFilters = [],
  appliedFilters = DEFAULT_FILTER_VALUES,
  onApplyFilters,
  onResetFilters,
  filterCount,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(appliedFilters);
  const containerRef = useRef(null);

  const count = filterCount ?? activeFilters.length;
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const openDropdown = () => {
    setDraftFilters(appliedFilters);
    setIsOpen(true);
  };

  const closeDropdown = () => setIsOpen(false);

  const toggleDropdown = () => (isOpen ? closeDropdown() : openDropdown());

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeDropdown();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeDropdown();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleFieldChange = (field) => (e) => {
    const { value } = e.target;

    setDraftFilters((prev) => {
      if (DATE_FIELDS.includes(field) && value > today) {
        return prev;
      }

      const next = { ...prev, [field]: value };

      if (field === 'startDate' && next.endDate && value > next.endDate) {
        next.endDate = value;
      }

      if (field === 'endDate' && next.startDate && value < next.startDate) {
        next.startDate = value;
      }

      return next;
    });
  };

  const handleClear = () => {
    setDraftFilters(DEFAULT_FILTER_VALUES);
    onResetFilters?.();
    closeDropdown();
  };

  const handleApply = () => {
    onApplyFilters?.(draftFilters);
    closeDropdown();
  };

  return (
    <FilterBarWrapper ref={containerRef}>
      <FilterControlsRow>
        <FilterTriggerButton
          type="button"
          onClick={toggleDropdown}
          aria-haspopup="true"
          aria-expanded={isOpen}
          $active={isOpen}
        >
          <TuneIcon />
          Filters
          {count > 0 && <FilterCountBadge>{count}</FilterCountBadge>}
        </FilterTriggerButton>

        {isOpen && (
          <FilterDropdown role="dialog" aria-label="Filter tickets">
            <FilterDropdownHeader>
              <FilterDropdownTitle>Filter tickets</FilterDropdownTitle>
              <FilterDropdownClose type="button" aria-label="Close filters" onClick={closeDropdown}>
                <CloseIcon />
              </FilterDropdownClose>
            </FilterDropdownHeader>

            <FilterSection>
              <FilterSectionLabel htmlFor="status-filter">Status</FilterSectionLabel>
              <FilterSelect
                id="status-filter"
                value={draftFilters.status}
                onChange={handleFieldChange('status')}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FilterSelect>
            </FilterSection>

            <FilterSection>
              <FilterSectionLabel htmlFor="ticket-type-filter">Ticket type</FilterSectionLabel>
              <FilterSelect
                id="ticket-type-filter"
                value={draftFilters.type}
                onChange={handleFieldChange('type')}
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FilterSelect>
            </FilterSection>

            <FilterSection>
              <FilterSectionLabel htmlFor="start-date-filter">
                Date range (From Date - To Date)
              </FilterSectionLabel>
              <DateRangeRow>
                <DateInput
                  id="start-date-filter"
                  type="date"
                  aria-label="Start date"
                  value={draftFilters.startDate}
                  max={draftFilters.endDate || today}
                  onChange={handleFieldChange('startDate')}
                />
                <DateRangeSeparator>–</DateRangeSeparator>
                <DateInput
                  id="end-date-filter"
                  type="date"
                  aria-label="End date"
                  value={draftFilters.endDate}
                  min={draftFilters.startDate || undefined}
                  max={today}
                  onChange={handleFieldChange('endDate')}
                />
              </DateRangeRow>
            </FilterSection>

            <FilterDropdownFooter>
              <ClearFilterButton type="button" onClick={handleClear}>
                Clear filter
              </ClearFilterButton>
              <ApplyFilterButton type="button" onClick={handleApply}>
                Apply filter
              </ApplyFilterButton>
            </FilterDropdownFooter>
          </FilterDropdown>
        )}
      </FilterControlsRow>
    </FilterBarWrapper>
  );
};

export default FilterBar;
